// src/components/teachers/AddRouteToBus.js

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn as AssignIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DirectionsBus as DirectionsBusIcon,
  Route as RouteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import MuiAlert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import EmptyState from '../common/EmptyState';

// Styled Components for Enhanced Design
const PanelCard = styled(AdminPanelCard)(({ theme }) => ({
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const AddRouteToBus = () => {
  const [buses, setBuses] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [selectedBusId, setSelectedBusId] = useState('');
  const [selectedRouteIds, setSelectedRouteIds] = useState([]);
  const [assignedRoutes, setAssignedRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const snackbarRef = useRef(); // Create a ref for the Snackbar

  // Retrieve schoolId from localStorage
  const schoolId = localStorage.getItem('schoolId');

  useEffect(() => {
    fetchBuses();
    fetchRoutes();
  }, [refreshTrigger]);

  // Reusable Snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    if (snackbarRef.current) {
      // Make sure that you show the alert in your code.
    }
  };

  // Function to fetch buses from the API
  const fetchBuses = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/api/buses', { headers: { schoolId } });
      setBuses(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching buses:', err);
      setError('Failed to fetch buses.');
      showSnackbar('Error fetching buses.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch all available routes from the backend
  const fetchRoutes = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get('/api/routes', { headers: { schoolId } });
      setRoutes(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching routes:', err);
      setError('Failed to fetch routes.');
      showSnackbar('Error fetching routes.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch assigned routes when a bus is selected
  useEffect(() => {
    if (selectedBusId) {
      fetchAssignedRoutes(selectedBusId);
    } else {
      setAssignedRoutes([]);
    }
  }, [selectedBusId, refreshTrigger]);

  // Function to fetch assigned routes for a specific bus
  const fetchAssignedRoutes = async (busId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/api/buses/${busId}`, { headers: { schoolId } });
      setAssignedRoutes(response.data.routes || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching assigned routes:', err);
      setError('Failed to fetch assigned routes.');
      showSnackbar('Error fetching assigned routes.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bus selection
  const handleBusChange = (event) => {
    setSelectedBusId(event.target.value);
    setSelectedRouteIds([]); // Reset selected routes
  };

  // Handle route selection (multiple selection)
  const handleRouteSelect = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedRouteIds(
      typeof value === 'string' ? value.split(',') : value
    );
  };

  // Assign selected routes to the bus
  const handleAssignRoutes = async () => {
    if (!selectedBusId || selectedRouteIds.length === 0) {
      setError('Please select a bus and at least one route.');
      return;
    }

    setAssigning(true);
    setError(null);

    try {
      // Ensure no duplicate assignments
      const alreadyAssignedRouteIds = new Set(assignedRoutes.map((route) => route.routeId));
      const uniqueRouteIds = selectedRouteIds.filter((id) => !alreadyAssignedRouteIds.has(id));

      if (uniqueRouteIds.length === 0) {
        setError('Selected routes are already assigned to this bus.');
        setAssigning(false);
        return;
      }

      // Backend expects a Set<Long>, but JSON does not have a Set type. Convert to array.
      await axiosInstance.put(`/api/buses/${selectedBusId}/assign-routes`, uniqueRouteIds, { headers: { schoolId } });
      fetchAssignedRoutes(selectedBusId); // Refresh assigned routes
      setSelectedRouteIds([]); // Reset selected routes
      showSnackbar('Routes Assigned Successfully!', 'success');
    } catch (err) {
      console.error('Error assigning routes to bus:', err);
      const errorMessage = err.response?.data?.message || 'Failed to assign routes to bus.';
      setError(errorMessage);
      showSnackbar('Failed to assign routes to bus', 'error');
    } finally {
      setAssigning(false);
    }
  };

  // Unassign a route from the bus
  const handleUnassignRoute = async (routeId) => {
    if (!selectedBusId) {
      setError('Please select a bus.');
      return;
    }

    // Confirm unassignment
    if (!window.confirm('Are you sure you want to unassign this route from the bus?')) {
      return;
    }

    setUnassigning(true);
    setError(null);

    try {
      await axiosInstance.post(`/api/buses/${selectedBusId}/remove-routes`, [routeId], { headers: { schoolId } });
      fetchAssignedRoutes(selectedBusId); // Refresh assigned routes
      showSnackbar('Route unassigned successfully', 'success');
    } catch (err) {
      console.error('Error unassigning route from bus:', err);
      const errorMessage = err.response?.data?.message || 'Failed to unassign route from bus.';
      setError(errorMessage);
      showSnackbar('Failed to remove routes to bus', 'error');
    } finally {
      setUnassigning(false);
    }
  };

  // Handle Snackbar close
  const handleSnackbarClose = (event, reason) => { // Add reason parameter here
    if (reason === 'clickaway') return;
    setError(null);
    setSnackbarOpen(false);
  };

  // Refresh data manually
  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
      <Box sx={{ width: '100%' }}>
        <AdminPageHero
          title="Assign Routes to Bus"
          subtitle="Link buses to routes and keep transportation assignments current."
          metrics={[
            { label: 'Buses', value: buses.length },
            { label: 'Routes', value: routes.length },
            { label: 'Assigned', value: assignedRoutes.length },
          ]}
        />

        <Box sx={{ mt: 3 }}>
          <Stack spacing={3}>
            <PanelCard
              title="Route Assignment"
              subtitle="Choose a bus, review mapped routes, and assign additional routes."
            >
              {error && (
                <Alert severity="error" onClose={handleSnackbarClose} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <FormControl fullWidth size="small">
                <InputLabel id="bus-select-label">Select Bus</InputLabel>
                <Select
                  labelId="bus-select-label"
                  id="bus-select"
                  value={selectedBusId}
                  label="Select Bus"
                  onChange={handleBusChange}
                >
                  {buses.length > 0 ? (
                    buses.map((bus) => (
                      <MenuItem key={bus.busId} value={bus.busId}>
                        <Box display="flex" alignItems="center">
                          <DirectionsBusIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="body1">
                            {bus.busNumber} - {bus.driverName || 'No driver name'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      <em>No buses available</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </PanelCard>

            {selectedBusId && (
              <PanelCard
                title="Assigned Routes"
                subtitle="Current route mappings for the selected bus."
                action={
                  <Tooltip title="Refresh Assigned Routes">
                    <IconButton onClick={handleRefresh} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                }
                contentSx={{ p: 0 }}
              >
                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                    <CircularProgress />
                  </Box>
                ) : assignedRoutes.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b' }}>Route ID</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b' }}>Route Name</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b' }}>Start Location</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b' }}>End Location</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, color: '#64748b' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assignedRoutes.map((route) => (
                          <TableRow key={route.routeId} hover>
                            <TableCell align="center">{route.routeId}</TableCell>
                            <TableCell align="center">{route.routeName}</TableCell>
                            <TableCell align="center">{route.startLocation}</TableCell>
                            <TableCell align="center">{route.endLocation}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="Unassign Route">
                                <IconButton
                                  color="error"
                                  onClick={() => handleUnassignRoute(route.routeId)}
                                  disabled={unassigning}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ p: 2.4 }}>
                    <EmptyState
                      title="No routes assigned"
                      description="Assign one or more routes below to activate this bus."
                    />
                  </Box>
                )}
              </PanelCard>
            )}

            <PanelCard
              title="Assign New Routes"
              subtitle="Select one or more route plans for the chosen bus."
            >
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="route-select-label">Select Routes</InputLabel>
                  <Select
                    labelId="route-select-label"
                    id="route-select"
                    multiple
                    value={selectedRouteIds}
                    onChange={handleRouteSelect}
                    label="Select Routes"
                    renderValue={(selected) =>
                      routes
                        .filter((route) => selected.includes(route.routeId))
                        .map((route) => route.routeName)
                        .join(', ')
                    }
                  >
                    {routes.length > 0 ? (
                      routes.map((route) => (
                        <MenuItem key={route.routeId} value={route.routeId}>
                          <Checkbox checked={selectedRouteIds.indexOf(route.routeId) > -1} />
                          <Box display="flex" alignItems="center">
                            <RouteIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="body2">
                              {route.routeName} ({route.startLocation} to {route.endLocation})
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        <em>No routes available</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AssignIcon />}
                    onClick={handleAssignRoutes}
                    disabled={assigning || selectedRouteIds.length === 0}
                    sx={{ fontWeight: 700 }}
                  >
                    Assign Selected Routes
                  </Button>
                  {assigning && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Box>
              </Stack>
            </PanelCard>
          </Stack>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            
            <Alert
              ref={snackbarRef} // Attach the ref here
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              action={
                <React.Fragment>
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    sx={{ p: 0.5 }}
                    onClick={handleSnackbarClose}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </React.Fragment>
              }
               sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
  );
};

export default AddRouteToBus;
